import { IconButton, makeStyles } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import DeleteIcon from '@material-ui/icons/Delete';
import PrintIcon from '@material-ui/icons/Print';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import { Document, Image as PDFImage, Page, pdf } from '@react-pdf/renderer';
import { useStores } from 'doc-mate-store/lib/hooks';
import { saveAs } from 'file-saver';
import printJS from 'print-js';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import Gallery, { PhotoProps } from 'react-photo-gallery';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

type Props = {
  caption: string;
  photos: {
    id: string | number;
    src: string;
    thumbnail: string;
    caption?: string;
    width: number;
    height: number;
    rotate?: number;
  }[];
  onAdd: () => void;
  document: any;
  canUpdate: boolean;
};

type PhotoThumbnailProps = {
  index: number;
  photo: PhotoProps;
  rotateValue: number;
  canUpdate: boolean;
  isBeingDragged: boolean;
  isTargetOver: boolean;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onClick: (e: React.MouseEvent, index: number) => void;
  classes: any;
};

const PhotoThumbnail: React.FC<PhotoThumbnailProps> = ({
  index,
  photo,
  rotateValue,
  canUpdate,
  isBeingDragged,
  isTargetOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  classes,
}) => {
  const rotateStyle =
    rotateValue === 0
      ? classes.thumbnailRotateTo0
      : rotateValue === 90
      ? classes.thumbnailRotateTo90
      : rotateValue === 180
      ? classes.thumbnailRotateTo180
      : classes.thumbnailRotateTo270;

  const aspectRatio = photo.width / photo.height;

  return (
    <span
      className={`${classes.photoContainer} ${
        isTargetOver ? classes.photoContainerDragOver : ''
      } ${isBeingDragged ? classes.photoContainerDragging : ''}`}
      draggable={canUpdate}
      onDragStart={e => onDragStart(e, index)}
      onDragOver={e => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={e => onDrop(e, index)}
    >
      <img
        draggable={false}
        className={[classes.photo, rotateStyle].join(' ')}
        src={photo.src}
        width={
          rotateValue === 90 || rotateValue === 270
            ? Math.min((1 / aspectRatio) * 64, 64)
            : Math.min(aspectRatio * 64, 64)
        }
        onClick={e => onClick(e, index)}
        alt="img"
      />
    </span>
  );
};

function printResource(url: string, header: string, rotate: number) {
  printJS({
    printable: url,
    type: 'image',
    imageStyle: `
    max-height: 95%;
    max-width: 95%;
    display: block;
    margin: auto;
    transform: rotate(${rotate}deg);
  `,
  });
}

const LoadDocumentPhotoList: React.FC<Props> = ({
  caption,
  photos,
  onAdd,
  document,
  canUpdate,
}) => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [rotate, setRotate] = useState(photos.map(photo => photo.rotate || 0));
  const [isOpen, setIsOpen] = useState(false);
  const [currentPhotoRotate, setCurrentPhotoRotate] = useState(
    rotate[currentPhoto] || 0,
  );

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const slides = photos.map(photo => ({
    src: photo.src,
    width: photo.width,
    height: photo.height,
    alt: photo.caption || caption,
  }));

  const galleryPhotos = photos.map(photo => ({
    src: photo.thumbnail,
    width: photo.width,
    height: photo.height,
  }));

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (!canUpdate) return;
      e.stopPropagation();
      e.dataTransfer.setData('application/x-reorder-photo', `${index}`);
      e.dataTransfer.setData('text/plain', `${index}`);
      e.dataTransfer.effectAllowed = 'move';
      setDraggedIndex(index);
    },
    [canUpdate],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      if (!canUpdate) return;
      const types = Array.from(e.dataTransfer.types || []);
      if (!types.includes('application/x-reorder-photo')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [canUpdate, dragOverIndex],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      if (!canUpdate) return;
      const types = Array.from(e.dataTransfer.types || []);
      if (!types.includes('application/x-reorder-photo')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      setDragOverIndex(null);
      setDraggedIndex(null);

      const sourceIndexStr = e.dataTransfer.getData(
        'application/x-reorder-photo',
      );
      const sourceIndex = sourceIndexStr
        ? parseInt(sourceIndexStr, 10)
        : draggedIndex;

      if (
        sourceIndex === null ||
        isNaN(sourceIndex) ||
        sourceIndex === targetIndex
      ) {
        return;
      }

      const updatedPhotos = [...photos];
      const [movedPhoto] = updatedPhotos.splice(sourceIndex, 1);
      updatedPhotos.splice(targetIndex, 0, movedPhoto);

      const reorderedIds = updatedPhotos.map(p => p.id);
      if (document && typeof document.reorderPhotos === 'function') {
        document.reorderPhotos(reorderedIds);
      }
    },
    [canUpdate, draggedIndex, photos, document],
  );

  const handleThumbnailClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      e.stopPropagation();
      setCurrentPhoto(index);
      setCurrentPhotoRotate(rotate[index] || photos[index]?.rotate || 0);
      setIsOpen(true);
    },
    [photos, rotate],
  );

  const handleRotate = useCallback(() => {
    setRotate(prevState => {
      const nextState = [...prevState];
      nextState[currentPhoto] =
        nextState[currentPhoto] === 270 ? 0 : nextState[currentPhoto] + 90;
      return nextState;
    });
    setCurrentPhotoRotate(prevState =>
      prevState === 270 ? 0 : prevState + 90,
    );
    rootStore.rotatePhotoRight(`${photos[currentPhoto].id}`);
  }, [currentPhoto, photos, rootStore]);

  const handlePrint = useCallback(() => {
    printResource(photos[currentPhoto].src, caption, rotate[currentPhoto]);
  }, [caption, currentPhoto, photos, rotate]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handlePDFSave = useCallback(async () => {
    const orientation =
      photos[currentPhoto].width > photos[currentPhoto].height
        ? 'landscape'
        : 'portrait';
    const PDFPhoto = () => (
      <Document>
        <Page style={{ padding: 10 }} orientation={orientation}>
          <PDFImage src={photos[currentPhoto].src} fixed />
        </Page>
      </Document>
    );

    const blob = await pdf(<PDFPhoto />).toBlob();
    saveAs(blob, `${caption} - ${photos[currentPhoto].id}`);
  }, [caption, currentPhoto, photos]);

  const handleDelete = useCallback(async () => {
    await document.deleteDocumentPhoto(photos[currentPhoto].id);
  }, [currentPhoto, document, photos]);

  const handleDeleteConfirm = useCallback(async () => {
    confirmAlert({
      title: 'Confirm to Delete',
      message: 'Are you sure you want to delete this photo?',
      buttons: [
        { label: 'Confirm', onClick: () => handleDelete() },
        { label: 'Cancel' },
      ],
      overlayClassName: 'react-confirm-alert-overlay-custom',
    });
  }, [handleDelete]);

  const handleAddClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAdd();
    },
    [onAdd],
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!!galleryPhotos.length && (
        <Gallery
          photos={galleryPhotos}
          renderImage={props => {
            const rotateValue =
              photos[props.index]?.rotate || rotate[props.index] || 0;
            return (
              <PhotoThumbnail
                key={photos[props.index]?.id || props.index}
                index={props.index}
                photo={props.photo}
                rotateValue={rotateValue}
                canUpdate={canUpdate}
                isBeingDragged={draggedIndex === props.index}
                isTargetOver={dragOverIndex === props.index}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleThumbnailClick}
                classes={classes}
              />
            );
          }}
        />
      )}
      {canUpdate && (
        <span onClick={handleAddClick} className={classes.add}>
          +
        </span>
      )}
      <Lightbox
        open={isOpen}
        close={handleClose}
        index={currentPhoto}
        slides={slides}
        on={{
          view: ({ index }) => {
            setCurrentPhoto(index);
            setCurrentPhotoRotate(rotate[index] || 0);
          },
        }}
        styles={{
          root: { zIndex: 1500 },
          slide: {
            transform: `rotate(${currentPhotoRotate}deg)`,
            transition: 'transform 0.2s ease',
          },
        }}
        toolbar={{
          buttons: [
            <IconButton
              key="rotate-control"
              aria-label="rotate"
              className={classes.control}
              onClick={handleRotate}
              title="Rotate"
            >
              <RotateRightIcon fontSize="inherit" />
            </IconButton>,
            <IconButton
              key="print-control"
              aria-label="print"
              className={classes.control}
              onClick={handlePrint}
              title="Print"
            >
              <PrintIcon fontSize="inherit" />
            </IconButton>,
            <IconButton
              key="download-control"
              aria-label="download"
              className={classes.control}
              onClick={handlePDFSave}
              title="Download"
            >
              <CloudDownloadIcon fontSize="inherit" />
            </IconButton>,
            ...(canUpdate
              ? [
                  <IconButton
                    key="delete-control"
                    aria-label="delete"
                    className={classes.control}
                    onClick={handleDeleteConfirm}
                    title="Delete"
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>,
                ]
              : []),
            'close',
          ],
        }}
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  control: {
    color: 'white',
  },
  photoContainer: {
    marginRight: 5,
    display: 'inline-block',
    transition: 'all 0.15s ease-in-out',
  },
  photoContainerDragOver: {
    outline: '2px dashed #0d374b',
    borderRadius: 4,
    transform: 'scale(1.08)',
  },
  photoContainerDragging: {
    opacity: 0.4,
  },
  photo: {
    maxHeight: 64,
    maxWidth: 64,
    cursor: 'pointer',
    position: 'relative',
    transition: 'transform 0.1s',
    objectFit: 'contain',
  },
  photoList: {
    overflow: 'visible',
  },
  thumbnailRotateTo0: {
    transform: `rotate(0deg)`,
    '&:hover': {
      transform: 'scale(1.2) rotate(0deg)',
      transition: 'transform 0.1s',
      zIndex: 1100,
    },
  },
  thumbnailRotateTo90: {
    transform: `rotate(90deg)`,
    '&:hover': {
      transform: 'scale(1.2) rotate(90deg)',
      transition: 'transform 0.1s',
      zIndex: 1100,
    },
  },
  thumbnailRotateTo180: {
    transform: `rotate(180deg)`,
    '&:hover': {
      transform: 'scale(1.2) rotate(180deg)',
      transition: 'transform 0.1s',
      zIndex: 1100,
    },
  },
  thumbnailRotateTo270: {
    transform: `rotate(270deg)`,
    '&:hover': {
      transform: 'scale(1.2) rotate(270deg)',
      transition: 'transform 0.1s',
      zIndex: 1100,
    },
  },
  add: {
    fontSize: 16,
    fontWeight: 600,
    paddingLeft: 4,
    cursor: 'pointer',
    '&:hover': {
      fontSize: 20,
    },
  },
}));

export default LoadDocumentPhotoList;
